import React from "react";
import { Link, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useRequest } from "../hooks/useRequest";
import { useAuth } from "../context/AuthContext";
import { useLeaguePublicContext } from "./LeaguePublicLayout";
import { getSeasonPlayerStatsUrl } from "../config/api";
import { PlayerStat, GoalieStat } from "../types/models";

type SkaterSortColumn =
  | "points"
  | "goals"
  | "assists"
  | "games_played"
  | "ppg"
  | "penalties"
  | "penalty_minutes"
  | "team_name";
type GoalieSortColumn =
  | "games_played"
  | "wins"
  | "losses"
  | "ties"
  | "goals_against"
  | "goals_against_average"
  | "shutouts"
  | "goals"
  | "assists"
  | "penalty_minutes"
  | "team_name";
type SortDirection = "asc" | "desc";

function StatsPage() {
  const auth = useAuth();
  const { abbr } = useParams<{ abbr: string }>();
  const { seasonId, gameTypeFilter, currentSeasonGameType, includeSpares } =
    useLeaguePublicContext();
  const [statType, setStatType] = React.useState<"skater" | "goalie">("skater");
  const [skaterSortColumn, setSkaterSortColumn] =
    React.useState<SkaterSortColumn>("points");
  const [goalieSortColumn, setGoalieSortColumn] =
    React.useState<GoalieSortColumn>("wins");
  const [sortDirection, setSortDirection] =
    React.useState<SortDirection>("desc");

  // For stats page, we only use "regular" or "playoff" (never "all")
  // If filter is "all", default to currentSeasonGameType or "regular"
  const effectiveGameType: "regular" | "playoff" =
    gameTypeFilter === "regular" || gameTypeFilter === "playoff"
      ? gameTypeFilter
      : currentSeasonGameType === "playoff"
        ? "playoff"
        : "regular";

  // Player stats — filtered by game type
  // Endpoint: GET /seasons/:id/player_stats?game_type=regular
  const skaterStatsUrl = seasonId
    ? getSeasonPlayerStatsUrl(seasonId, effectiveGameType, "skater")
    : undefined;
  const { data: skaterStatsResponse } = useRequest<PlayerStat[]>(
    skaterStatsUrl,
    auth.token,
  );

  // Goalie stats — filtered by game type
  // Endpoint: GET /seasons/:id/player_stats?game_type=regular&stat_type=goalie
  const goalieStatsUrl = seasonId
    ? getSeasonPlayerStatsUrl(seasonId, effectiveGameType, "goalie")
    : undefined;
  const { data: goalieStatsResponse } = useRequest<GoalieStat[]>(
    goalieStatsUrl,
    auth.token,
  );

  // Handle column header click for sorting
  const handleSkaterSort = (column: SkaterSortColumn) => {
    if (skaterSortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Default to descending for new column (except games_played which defaults to ascending)
      setSkaterSortColumn(column);
      setSortDirection(column === "games_played" ? "asc" : "desc");
    }
  };

  const handleGoalieSort = (column: GoalieSortColumn) => {
    if (goalieSortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Default to descending for new column (except games_played which defaults to ascending)
      setGoalieSortColumn(column);
      setSortDirection(column === "games_played" ? "asc" : "desc");
    }
  };

  // Filter out spare players if includeSpares is false (for skaters)
  const filteredSkaterStats = includeSpares
    ? skaterStatsResponse ?? []
    : (skaterStatsResponse ?? []).filter((stat) => !stat.spare_player);

  // Filter out spare players if includeSpares is false (for goalies)
  const filteredGoalieStats = includeSpares
    ? goalieStatsResponse ?? []
    : (goalieStatsResponse ?? []).filter((stat) => !stat.spare_player);

  // Sort skater stats based on selected column and direction
  const skaterLeaderboard = [...filteredSkaterStats].sort((a, b) => {
    let diff: number;

    if (skaterSortColumn === "team_name") {
      // String comparison for team name; treat null/undefined as empty string
      const nameA = a.team_name || "";
      const nameB = b.team_name || "";
      diff =
        sortDirection === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
    } else {
      let valA: number;
      let valB: number;

      if (skaterSortColumn === "ppg") {
        valA = a.games_played > 0 ? a.points / a.games_played : 0;
        valB = b.games_played > 0 ? b.points / b.games_played : 0;
      } else {
        valA = a[skaterSortColumn];
        valB = b[skaterSortColumn];
      }

      diff = sortDirection === "asc" ? valA - valB : valB - valA;
    }

    // Secondary sort: if values are equal, sort by points (desc), then goals (desc)
    if (diff === 0 && skaterSortColumn !== "points") {
      const ptsDiff = b.points - a.points;
      if (ptsDiff !== 0) return ptsDiff;
      if (skaterSortColumn !== "goals") {
        return b.goals - a.goals;
      }
    }

    return diff;
  });

  // Sort goalie stats based on selected column and direction
  const goalieLeaderboard = [...filteredGoalieStats].sort((a, b) => {
    let diff: number;

    if (goalieSortColumn === "team_name") {
      // String comparison for team name; treat null/undefined as empty string
      const nameA = a.team_name || "";
      const nameB = b.team_name || "";
      diff =
        sortDirection === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
    } else {
      const valA = a[goalieSortColumn];
      const valB = b[goalieSortColumn];
      diff = sortDirection === "asc" ? valA - valB : valB - valA;
    }

    // Secondary sort: if values are equal, sort by wins (desc), then GAA (asc)
    if (diff === 0 && goalieSortColumn !== "wins") {
      const winsDiff = b.wins - a.wins;
      if (winsDiff !== 0) return winsDiff;
      if (goalieSortColumn !== "goals_against_average") {
        return a.goals_against_average - b.goals_against_average;
      }
    }

    return diff;
  });

  if (!seasonId) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  if (!skaterStatsResponse || !goalieStatsResponse) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  const currentLeaderboard =
    statType === "skater" ? skaterLeaderboard : goalieLeaderboard;

  return (
    <div>
      {/* Stat Type Filter */}
      <div style={{ marginBottom: "16px" }}>
        <div
          className="lp-game-type-filter"
          role="radiogroup"
          aria-label="Filter by stat type"
        >
          <button
            type="button"
            role="radio"
            aria-checked={statType === "skater"}
            className={`lp-filter-btn${statType === "skater" ? " active" : ""}`}
            onClick={() => setStatType("skater")}
          >
            Skaters
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={statType === "goalie"}
            className={`lp-filter-btn${statType === "goalie" ? " active" : ""}`}
            onClick={() => setStatType("goalie")}
          >
            Goalies
          </button>
        </div>
      </div>

      {/* Stats Table */}
      <div className="lp-card">
        {currentLeaderboard.length === 0 ? (
          <div className="lp-card-empty">
            {statType === "skater"
              ? "No player stats yet."
              : "No goalie stats yet."}
          </div>
        ) : statType === "skater" ? (
          <table className="lp-table" aria-label="Player statistics">
            <thead>
              <tr>
                <th className="lp-th-left" style={{ width: "18px" }}></th>
                <th className="lp-th-left">Player</th>
                <th
                  className="lp-th-left"
                  title="Team"
                  onClick={() => handleSkaterSort("team_name")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Team{" "}
                  {skaterSortColumn === "team_name" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Games played"
                  onClick={() => handleSkaterSort("games_played")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  GP{" "}
                  {skaterSortColumn === "games_played" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Goals"
                  onClick={() => handleSkaterSort("goals")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  G{" "}
                  {skaterSortColumn === "goals" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Assists"
                  onClick={() => handleSkaterSort("assists")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  A{" "}
                  {skaterSortColumn === "assists" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="lp-th-pts"
                  title="Points"
                  onClick={() => handleSkaterSort("points")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  PTS{" "}
                  {skaterSortColumn === "points" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="lp-th-mobile-hide"
                  title="Points per game"
                  onClick={() => handleSkaterSort("ppg")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  PPG{" "}
                  {skaterSortColumn === "ppg" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="lp-th-mobile-hide"
                  title="Penalties"
                  onClick={() => handleSkaterSort("penalties")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  PEN{" "}
                  {skaterSortColumn === "penalties" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="lp-th-mobile-hide"
                  title="Penalty minutes"
                  onClick={() => handleSkaterSort("penalty_minutes")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  PIM{" "}
                  {skaterSortColumn === "penalty_minutes" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
              </tr>
            </thead>
            <tbody>
              {skaterLeaderboard.map((row, i) => {
                const ppg =
                  row.games_played > 0
                    ? (row.points / row.games_played).toFixed(2)
                    : "0.00";
                return (
                  <tr key={row.player_id}>
                    <td className="lp-td-left lp-rank">{i + 1}</td>
                    <td className="lp-td-left">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Link
                          to={`/leagues/${abbr}/players/${row.player_id}`}
                          style={{
                            color: "var(--text-primary)",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                        >
                          {row.player_name}
                        </Link>
                        {row.spare_player && (
                          <span
                            title="Spare Player"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "16px",
                              height: "16px",
                              fontSize: "9px",
                              fontWeight: 700,
                              color: "var(--bg-primary)",
                              backgroundColor: "var(--text-secondary)",
                              borderRadius: "3px",
                              cursor: "help",
                            }}
                          >
                            S
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className="lp-td-left"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {row.team_name || "—"}
                    </td>
                    <td style={{ color: "var(--text-secondary)" }}>
                      {row.games_played}
                    </td>
                    <td>{row.goals}</td>
                    <td>{row.assists}</td>
                    <td className="lp-td-pts lp-pts">{row.points}</td>
                    <td
                      className="lp-td-mobile-hide"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {ppg}
                    </td>
                    <td className="lp-td-mobile-hide">{row.penalties}</td>
                    <td className="lp-td-mobile-hide">{row.penalty_minutes}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <table className="lp-table" aria-label="Goalie statistics">
            <thead>
              <tr>
                <th className="lp-th-left" style={{ width: "18px" }}></th>
                <th className="lp-th-left">Player</th>
                <th
                  className="lp-th-left"
                  title="Team"
                  onClick={() => handleGoalieSort("team_name")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Team{" "}
                  {goalieSortColumn === "team_name" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Games played"
                  onClick={() => handleGoalieSort("games_played")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  GP{" "}
                  {goalieSortColumn === "games_played" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Wins"
                  onClick={() => handleGoalieSort("wins")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  W{" "}
                  {goalieSortColumn === "wins" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Losses"
                  onClick={() => handleGoalieSort("losses")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  L{" "}
                  {goalieSortColumn === "losses" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="lp-th-mobile-hide"
                  title="Ties"
                  onClick={() => handleGoalieSort("ties")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  T{" "}
                  {goalieSortColumn === "ties" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="lp-th-mobile-hide"
                  title="Goals Against"
                  onClick={() => handleGoalieSort("goals_against")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  GA{" "}
                  {goalieSortColumn === "goals_against" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Goals Against Average"
                  onClick={() => handleGoalieSort("goals_against_average")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  GAA{" "}
                  {goalieSortColumn === "goals_against_average" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Shutouts"
                  onClick={() => handleGoalieSort("shutouts")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  SO{" "}
                  {goalieSortColumn === "shutouts" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Goals"
                  onClick={() => handleGoalieSort("goals")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  G{" "}
                  {goalieSortColumn === "goals" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  title="Assists"
                  onClick={() => handleGoalieSort("assists")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  A{" "}
                  {goalieSortColumn === "assists" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  className="lp-th-mobile-hide"
                  title="Penalty minutes"
                  onClick={() => handleGoalieSort("penalty_minutes")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  PIM{" "}
                  {goalieSortColumn === "penalty_minutes" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
              </tr>
            </thead>
            <tbody>
              {goalieLeaderboard.map((row, i) => {
                return (
                  <tr key={row.player_id}>
                    <td className="lp-td-left lp-rank">{i + 1}</td>
                    <td className="lp-td-left">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Link
                          to={`/leagues/${abbr}/players/${row.player_id}`}
                          style={{
                            color: "var(--text-primary)",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                        >
                          {row.player_name}
                        </Link>
                        {row.spare_player && (
                          <span
                            title="Spare Player"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "16px",
                              height: "16px",
                              fontSize: "9px",
                              fontWeight: 700,
                              color: "var(--bg-primary)",
                              backgroundColor: "var(--text-secondary)",
                              borderRadius: "3px",
                              cursor: "help",
                            }}
                          >
                            S
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className="lp-td-left"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      {row.team_name || "—"}
                    </td>
                    <td style={{ color: "var(--text-secondary)" }}>
                      {row.games_played}
                    </td>
                    <td>{row.wins}</td>
                    <td>{row.losses}</td>
                    <td className="lp-td-mobile-hide">{row.ties}</td>
                    <td className="lp-td-mobile-hide">{row.goals_against}</td>
                    <td>{row.goals_against_average.toFixed(2)}</td>
                    <td>{row.shutouts}</td>
                    <td>{row.goals}</td>
                    <td>{row.assists}</td>
                    <td className="lp-td-mobile-hide">{row.penalty_minutes}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default StatsPage;
